import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCoachReviewMode } from '../hooks/useCoachReviewMode';

/**
 * Persistent corner pill shown only to pro-coach reviewers. Two purposes:
 *
 *   1. Informed-consent style — Ben is reminded that voice + typed
 *      feedback is being captured throughout the session.
 *   2. A manual "Flag last as feedback" button as a fallback for cases
 *      where the classifier got it wrong or the coach wants to be sure.
 *
 *  Rendered from AppShell so it floats over every page. No-ops when the
 *  current user isn't in review mode.
 */
export function CoachReviewBanner(): React.JSX.Element | null {
  const reviewMode = useCoachReviewMode();
  const navigate = useNavigate();
  const [flashing, setFlashing] = useState(false);

  if (!reviewMode.isReviewMode) return null;

  const handleFlag = async () => {
    await reviewMode.flagLastAsFeedback();
    setFlashing(true);
    window.setTimeout(() => setFlashing(false), 1500);
  };

  const handleReplay = () => {
    // Force-show the tour even after first_run_completed_at is set.
    // Clear any leftover step index so the replay starts at Step 1.
    if (typeof window !== 'undefined') window.sessionStorage.removeItem('firstRunStepIdx');
    navigate('/welcome?replay=1');
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="coach-review-banner"
      style={{
        // Top-centre so it doesn't compete with the floating Brain orb
        // in the bottom-right. Translucent pill keeps it unobtrusive.
        position: 'fixed',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 14px',
        background: 'rgba(20, 32, 50, 0.92)',
        color: '#fff',
        borderRadius: 999,
        fontSize: 12,
        fontFamily: 'system-ui, sans-serif',
        boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        backdropFilter: 'blur(8px)',
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: '#27ae60',
          boxShadow: '0 0 0 0 rgba(39,174,96,0.5)',
          animation: 'pulse 2s infinite',
        }}
      />
      <span>
        <strong>Voice feedback on</strong>
        <span style={{ opacity: 0.7 }}> — say "feedback:" to flag a thought, or hit the button →</span>
      </span>
      <button
        type="button"
        onClick={() => void handleFlag()}
        style={{
          padding: '4px 10px',
          fontSize: 11,
          background: flashing ? '#27ae60' : 'rgba(255,255,255,0.12)',
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          cursor: 'pointer',
          fontWeight: 600,
          transition: 'background 200ms',
        }}
      >
        {flashing ? 'Flagged ✓' : 'Flag last as feedback'}
      </button>
      <button
        type="button"
        onClick={handleReplay}
        data-testid="replay-tour"
        title="Replay the welcome tour"
        style={{
          padding: '4px 10px',
          fontSize: 11,
          background: 'rgba(255,255,255,0.08)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 999,
          cursor: 'pointer',
          fontWeight: 500,
        }}
      >
        ↺ Replay tour
      </button>
      <style>
        {`@keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(39,174,96,0.6); }
          70% { box-shadow: 0 0 0 8px rgba(39,174,96,0); }
          100% { box-shadow: 0 0 0 0 rgba(39,174,96,0); }
        }`}
      </style>
    </div>
  );
}
